// src/components/InstallBanner.jsx
import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useCookies } from '../context/CookieContext'; // ✅ Ruta correcta (sin 's')

const InstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);
  
  // ✅ Usar el contexto de cookies
  const { hasAccepted } = useCookies();

  // Color Brechó
  const brechoGreen = '#449879';
  const brechoGreenLight = '#e8f5f0';

  useEffect(() => {
    const detectDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      const ios = /iPad|iPhone|iPod/.test(userAgent) || 
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                  /Mac OS X/.test(userAgent) && navigator.maxTouchPoints > 1;
      
      const android = /android/i.test(userAgent);
      
      console.log('📱 iOS detectado:', ios);
      console.log('📱 Android detectado:', android);
      console.log('🍪 Cookies aceptadas:', hasAccepted);
      
      setIsIOS(ios);
      setIsAndroid(android);
      
      return { ios, android };
    };

    // Verificar si YA ESTÁ INSTALADA (modo standalone)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches;
    console.log('📱 Standalone mode:', isInStandaloneMode);

    if (isInStandaloneMode) {
      console.log('🚫 App ya instalada, no mostrar banner');
      setLoading(false);
      return;
    }

    // ✅ Si NO ha aceptado cookies, NO mostrar banner
    if (!hasAccepted) {
      console.log('🍪 Cookies no aceptadas, no mostrar banner');
      setLoading(false);
      return;
    }

    const { ios, android } = detectDevice();

    // Verificar si el banner fue cerrado anteriormente
    const dismissed = localStorage.getItem('installBannerDismissed');
    console.log('💾 Banner cerrado:', dismissed);

    if (dismissed === 'true') {
      console.log('❌ Banner fue cerrado anteriormente');
      setLoading(false);
      return;
    }

    if (ios || android) {
      console.log('✅ Dispositivo móvil detectado y cookies aceptadas, mostrando banner');
      setShowBanner(true);
    }

    setLoading(false);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      console.log('📦 Evento beforeinstallprompt capturado');
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [hasAccepted]);

  // iOS: Manejar clic en "Instalar App"
  const handleIOSInstall = () => {
    console.log('🔵 iOS: Usuario quiere instalar');
    setShowTutorial(true);
  };

  // iOS: Manejar clic en "Cancelar"
  const handleIOSCancel = () => {
    console.log('🔵 iOS: Usuario canceló instalación');
    setShowBanner(false);
    localStorage.setItem('installBannerDismissed', 'true');
  };

  const handleInstall = async () => {
    console.log('🔄 handleInstall llamado', { isAndroid, isIOS, hasDeferred: !!deferredPrompt });
    
    if (isAndroid && deferredPrompt) {
      console.log('🟢 Android: Instalando...');
      try {
        deferredPrompt.prompt();
        const result = await deferredPrompt.userChoice;
        console.log('📦 Resultado:', result);
        if (result.outcome === 'accepted') {
          setShowBanner(false);
          localStorage.setItem('installBannerDismissed', 'true');
        }
        setDeferredPrompt(null);
      } catch (error) {
        console.error('Error en instalación:', error);
        setShowBanner(false);
        localStorage.setItem('installBannerDismissed', 'true');
      }
    }
  };

  const handleClose = () => {
    console.log('❌ Cerrando banner manualmente');
    setShowBanner(false);
    localStorage.setItem('installBannerDismissed', 'true');
  };

  if (loading) return null;
  if (!showBanner) return null;

  return (
    <div className="fixed bottom-[15%] left-0 right-0 z-50 p-4">
      
      {/* 🟢 ANDROID - Instalación en 1 clic */}
      {isAndroid && (
        <div 
          className="text-white rounded-2xl p-6 max-w-md mx-auto relative shadow-2xl border animate-slide-up"
          style={{ backgroundColor: brechoGreen }}
        >
          <button 
            onClick={handleClose}
            className="absolute top-3 right-3 text-white/60 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
          
          <div className="flex flex-col items-center text-center gap-3">
            <div className="w-16 h-16 bg-white/15 rounded-full flex items-center justify-center backdrop-blur-sm">
              <img 
                src="/android.png" 
                alt="Android"
                className="w-8 h-8 object-contain"
              />
            </div>
            <div>
              <h3 className="font-bold text-lg">Brechó Vizinho</h3>
              <p className="text-xs opacity-80 mt-0.5">Instale o app para melhor experiência</p>
            </div>
            <button
              onClick={handleInstall}
              className="bg-white px-8 py-2.5 rounded-full font-semibold text-sm hover:bg-gray-50 transition-all transform hover:scale-[1.02] shadow-lg active:scale-95 w-full max-w-[200px]"
              style={{ color: brechoGreen }}
            >
              Instalar App
            </button>
          </div>
        </div>
      )}

      {/* 🔵 iOS - PRIMERO: Preguntar si quiere instalar */}
      {isIOS && !showTutorial && (
        <div className="bg-white rounded-2xl p-6 max-w-md mx-auto relative shadow-2xl border border-gray-200/80 animate-slide-up">
          <button 
            onClick={handleClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={18} />
          </button>
          
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center"
                style={{ backgroundColor: brechoGreenLight }}
              >
                <img 
                  src="/apple.png" 
                  alt="Apple"
                  className="w-8 h-8 object-contain"
                />
              </div>
            </div>
            
            <h2 className="text-xl font-bold text-gray-800">Instalar Brechó Vizinho</h2>
            <p className="text-sm text-gray-500 mt-2 mb-6">
              Deseja instalar o Brechó Vizinho no seu iPhone para ter uma experiência completa?
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={handleIOSCancel}
                className="flex-1 bg-gray-100 text-gray-600 px-4 py-2.5 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleIOSInstall}
                className="flex-1 px-4 py-2.5 rounded-full text-sm font-medium text-white transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg"
                style={{ backgroundColor: brechoGreen }}
              >
                Instalar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔵 iOS - SEGUNDO: Mostrar imagen instructivo */}
      {isIOS && showTutorial && (
        <div className="bg-white rounded-2xl p-5 max-w-md mx-auto relative shadow-2xl border border-gray-200/80 animate-slide-up">
          <button 
            onClick={handleClose}
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors z-10"
          >
            <X size={18} />
          </button>
          
          <div className="text-center mb-3">
            <div className="flex justify-center mb-2">
              <img 
                src="/apple.png" 
                alt="Apple"
                className="w-6 h-6 object-contain"
              />
            </div>
            <h2 className="text-lg font-bold text-gray-800">Como instalar no iPhone</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Siga o passo a passo na imagem abaixo
            </p>
          </div>

          <div className="rounded-xl overflow-hidden border border-gray-200/60">
            <img 
              src="/instructivo.jpeg" 
              alt="Instruções para instalar no iPhone"
              className="w-full h-auto object-contain"
              style={{ maxHeight: '65vh' }}
              onError={(e) => {
                console.error('❌ Error cargando imagen instructivo.jpeg');
                e.target.style.display = 'none';
                const parent = e.target.parentElement;
                const errorMsg = document.createElement('p');
                errorMsg.className = 'text-sm text-gray-500 p-4 text-center';
                errorMsg.textContent = '⚠️ Imagem não encontrada';
                parent.appendChild(errorMsg);
              }}
            />
          </div>

          <button
            onClick={handleClose}
            className="w-full mt-3 px-4 py-2.5 rounded-full text-sm font-medium text-white transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg"
            style={{ backgroundColor: brechoGreen }}
          >
            Entendi, vou instalar!
          </button>
        </div>
      )}
    </div>
  );
};

export default InstallBanner;